/**
 * describeResponseError — build a diagnostic message from a failed fetch Response.
 *
 * Exists because of task-9po368xgoh: a save failure was reported from production
 * carrying NO information at all. The call site did
 *
 *     const errorData = await response.json().catch(() => ({}));
 *     throw new Error(errorData.error || 'Failed to save configuration');
 *
 * which discards any error body that is not JSON — a gateway HTML page, a proxy
 * error, a 413 — and drops the HTTP status with it. Every distinct failure then
 * collapsed into one generic sentence, so the report arrived undiagnosable.
 *
 * Contract: ALWAYS include the status. Include the body when there is one,
 * preferring a JSON `error` field but falling back to the raw text rather than
 * discarding it — raw text is more information than a generic message, not less.
 */
export async function describeResponseError(
  response: Pick<Response, 'status' | 'statusText' | 'text'>,
  context?: string
): Promise<string> {
  let raw = '';
  try {
    raw = await response.text();
  } catch (readErr) {
    // Body unreadable (already consumed, network abort). Say so — do not pretend
    // there was no body, and do not lose the status by throwing from here.
    raw = `<response body unreadable: ${String(readErr)}>`;
  }

  let detail = raw.trim();
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      // THE ENVELOPE THIS BACKEND ACTUALLY RETURNS (backend-merged/routes/
      // content-config.js, every handler without exception):
      //     { error: '<generic category>', message: <the real exception> }
      // `error` is the CATEGORY — 'Validation error', 'Failed to set content
      // configuration' — and `message` carries the diagnostic. Preferring `error`
      // reproduces almost exactly the generic sentence this helper exists to kill,
      // so `message` wins. Both are shown when they differ, because the category is
      // useful context ('Validation error: scope is required').
      const cat = typeof parsed?.error === 'string' ? parsed.error.trim() : '';
      const msg = typeof parsed?.message === 'string' ? parsed.message.trim() : '';
      if (msg && cat && msg !== cat) detail = `${cat}: ${msg}`;
      else if (msg) detail = msg;
      else if (cat) detail = cat;
      // else: JSON with neither field — keep the raw text rather than losing it.
    } catch {
      // Body is not JSON — keep the raw text as the detail. This branch is the
      // whole point of the helper: it is where the old code threw information away.
    }
  }

  // Bound ONCE, at the end, so every path is truncated. Bounding per-branch is how
  // the JSON path previously escaped the limit and could emit an unbounded toast.
  if (detail.length > 300) detail = `${detail.slice(0, 300)}…`;

  const prefix = context ? `${context}: ` : '';
  const status = `HTTP ${response.status}${response.statusText ? ` ${response.statusText}` : ''}`;
  return `${prefix}${status}${detail ? ` — ${detail}` : ''}`;
}
